/* heapFileCache.js ─────────────────────────────────────────────────────────────
   Browser cache (IndexedDB) of downloaded heap parquet files, so reopening a
   heap skips the S3 download. Works on the main thread and inside workers.

   Entries are keyed `${heapId}:${fileType}` and stamped with the content
   version Maestro reports (S3 ETag — `download-url?includeVersions=true`). An
   entry is only served when its version matches the current one, because
   rebuilds overwrite some S3 keys in place. A null version is never cached.

   The cache is bounded (MAX_BYTES / MAX_ENTRIES, least-recently-used evicted)
   and cleared on sign-out (auth-hook logout), since heaps are client data.
   Bytes and bookkeeping live in separate stores so eviction never has to read
   the files themselves. Every operation is best-effort: any IndexedDB failure
   (private mode, quota, blocked) degrades to a cache miss, never an error.
   ──────────────────────────────────────────────────────────────────────────── */

const DB_NAME = 'orecasting-heap-files';
const FILES = 'files'; // { key, bytes: ArrayBuffer }
const META = 'meta';   // { key, version, size, lastUsed }
const MAX_BYTES = 400 * 1024 * 1024;
const MAX_ENTRIES = 24;

let dbPromise = null;

function openDb() {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve) => {
    try {
      if (typeof indexedDB === 'undefined') { resolve(null); return; }
      const req = indexedDB.open(DB_NAME, 1);
      req.onupgradeneeded = () => {
        const db = req.result;
        if (!db.objectStoreNames.contains(FILES)) db.createObjectStore(FILES, { keyPath: 'key' });
        if (!db.objectStoreNames.contains(META)) db.createObjectStore(META, { keyPath: 'key' });
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => resolve(null);
      req.onblocked = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
  return dbPromise;
}

const request = (req) => new Promise((resolve) => {
  req.onsuccess = () => resolve(req.result);
  req.onerror = () => resolve(null);
});

// Run fn inside one transaction over both stores; resolves with fn's result
// once the transaction commits (null on any failure).
async function withStores(mode, fn) {
  const db = await openDb();
  if (!db) return null;
  return new Promise((resolve) => {
    try {
      const tx = db.transaction([FILES, META], mode);
      let result = null;
      Promise.resolve(fn(tx.objectStore(FILES), tx.objectStore(META)))
        .then((r) => { result = r; }, () => { try { tx.abort(); } catch { /* already finished */ } });
      tx.oncomplete = () => resolve(result);
      tx.onerror = () => resolve(null);
      tx.onabort = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
}

/**
 * Cached bytes for `key` when the stored version equals `version`, else null.
 * @returns {Promise<Uint8Array|null>}
 */
export async function getCachedFile(key, version) {
  if (!version) return null;
  const bytes = await withStores('readwrite', async (files, meta) => {
    const m = await request(meta.get(key));
    if (!m || m.version !== version) return null;
    const f = await request(files.get(key));
    if (!f) return null;
    meta.put({ ...m, lastUsed: Date.now() });
    return f.bytes;
  });
  return bytes ? new Uint8Array(bytes) : null;
}

/**
 * Store a copy of `bytes` under `key` at `version`, then evict least-recently
 * used entries beyond the size / count caps.
 */
export async function putCachedFile(key, version, bytes) {
  if (!version || !bytes || bytes.byteLength > MAX_BYTES) return;
  const copy = bytes.slice().buffer; // the caller may transfer its buffer afterwards
  await withStores('readwrite', async (files, meta) => {
    files.put({ key, bytes: copy });
    meta.put({ key, version, size: copy.byteLength, lastUsed: Date.now() });
    const all = (await request(meta.getAll())) ?? [];
    all.sort((a, b) => b.lastUsed - a.lastUsed);
    let total = 0;
    all.forEach((e, i) => {
      total += e.size;
      if (i >= MAX_ENTRIES || total > MAX_BYTES) {
        files.delete(e.key);
        meta.delete(e.key);
      }
    });
  });
}

/** Drop every cached heap file (sign-out). */
export async function clearHeapFileCache() {
  await withStores('readwrite', (files, meta) => { files.clear(); meta.clear(); });
}
